import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keysToSnakeCase } from '~/util/convertKeys';

type SetQueryCallback<T> = (prev: Partial<T>) => Partial<T> | undefined;

const decodeQueryString = (value: string): string => {
  return value.replaceAll('%2C', ',').replaceAll('%3A', ':');
};

export const getQueryString = (query: Record<string, any> | undefined) => {
  if (!query) {
    return '';
  }

  const object = keysToSnakeCase(query);

  Object.keys(object).forEach((key) => (object[key] === undefined ? delete object[key] : {}));

  return object && Object.keys(object).length > 0
    ? `?${decodeQueryString(new URLSearchParams(object).toString())}`
    : '';
};

export const useQueryParams = <T extends Record<any, string | number | boolean>>() => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [queryObject, setQueryObjectState] = useState<Partial<T> | undefined>(undefined);

  const hasQueryParam = useCallback(
    (key: keyof T & string): boolean => {
      return searchParams.has(String(key));
    },
    [searchParams]
  );

  const getQueryParam = useCallback(
    (key: keyof T): string | null => {
      return searchParams.get(String(key));
    },
    [searchParams]
  );

  const setQueryParam = useCallback(
    (key: keyof T, value: T[keyof T]) => {
      searchParams.set(String(key), String(value));
      setSearchParams(searchParams);
    },
    [searchParams]
  );

  const removeQueryParam = useCallback(
    (key: keyof T) => {
      searchParams.delete(String(key));
      setSearchParams(searchParams);
    },
    [searchParams]
  );

  const setQueryObject = useCallback(
    (callback: SetQueryCallback<T>) => {
      const existingParams: Partial<T> = Array.from(searchParams.keys()).reduce<Partial<T>>((acc, key) => {
        if (key in acc) {
          acc[key as keyof T] = searchParams.get(key)! as unknown as T[keyof T];
        }
        return acc;
      }, {} as Partial<T>);

      const updatedParams = callback(existingParams);
      const newSearchParams = new URLSearchParams();

      if (typeof updatedParams === 'undefined') {
        setSearchParams(undefined);
        return;
      }

      for (const [key, value] of Object.entries(updatedParams)) {
        if (value !== null && value !== undefined) {
          newSearchParams.set(key, String(value));
        }
      }

      setSearchParams(newSearchParams);
    },
    [searchParams]
  );

  useEffect(() => {
    const newQueryParamsObject: Partial<T> = Array.from(searchParams.keys()).reduce<Partial<T>>((acc, key) => {
      acc[key as keyof T] = searchParams.get(key)! as unknown as T[keyof T];
      return acc;
    }, {} as Partial<T>);

    setQueryObjectState(Object.keys(newQueryParamsObject).length > 0 ? newQueryParamsObject : undefined);
  }, [searchParams]);

  return {
    hasQueryParam,
    getQueryParam,
    setQueryParam,
    removeQueryParam,
    queryObject,
    setQueryObject,
  };
};
